import { Dialog, DialogContent, makeStyles } from "@material-ui/core";
import React, { useEffect, useContext, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as callActions from "../../actions/callActions";
import * as requestActions from "../../actions/requestActions";
import RoomContext from "../../context/RoomContext";
import { intl } from "../../intl";
import {
  startCallRingtone,
  stopCallRingtone,
} from "../../helpers/callRingtone";
import InComingOutGoingCall from "../Management/Messaging/components/InComingOutGoingCall";
import { portalTokens as t } from "../../theme/portalTokens";

const useCallDialogStyles = makeStyles({
  backdrop: {
    backgroundColor: "rgba(2, 6, 23, 0.8)",
    backdropFilter: "blur(8px)",
  },
  paper: {
    borderRadius: 24,
    background: t.slate900,
    color: t.white,
    border: `1px solid ${t.slate800}`,
    padding: 24,
    textAlign: "center",
    maxWidth: 360,
    width: "calc(100% - 24px)",
    margin: 12,
    boxShadow: "0 25px 50px rgba(0,0,0,0.45)",
    overflow: "hidden",
    "body.is-electron-app.is-compact-mode &": {
      maxWidth: "100%",
      width: "100%",
      margin: 0,
      borderRadius: 0,
      border: "none",
      minHeight: "100%",
      boxSizing: "border-box",
    },
  },
  content: {
    padding: "0 !important",
  },
});

const CallView = () => {
  const classes = useCallDialogStyles();
  const dispatch = useDispatch();

  const [second, setSecond] = useState(0);
  const [milisecond, setMilisecond] = useState(0);

  const incomingCall = useSelector((state) => state.call.incomingCall);
  const currentCall = useSelector((state) => state.call.currentCall);
  const outgoingCall = useSelector((state) => state.call.outgoingCall);
  const room = useSelector((state) => state.room);

  const { roomClient } = useContext(RoomContext);

  useEffect(() => {
    if (incomingCall) {
      startCallRingtone();

      // Electron: çalma ekranı kompakt kalsın; büyütme sadece cevaplada
      if (window.wunityDesktop && window.wunityDesktop.isElectron) {
        if (
          window.__portalUI &&
          typeof window.__portalUI.enterCompact === "function"
        ) {
          window.__portalUI.enterCompact();
        } else if (typeof window.wunityDesktop.setWindowMode === "function") {
          window.wunityDesktop.setWindowMode("compact");
          if (typeof window.wunityDesktop.showWindow === "function") {
            window.wunityDesktop.showWindow();
          }
        }
      } else if (typeof Notification !== "undefined") {
        // Browser: native Notification for focus
        const body = intl.formatMessage({
          id: `messaging.inComing`,
          defaultMessage: "Incoming Call",
        });
        const groupName = incomingCall?.payload?.group?.name;
        const notification = new Notification("Uçurtma", {
          body: groupName ? `${groupName} — ${body}` : body,
        });
        notification.onclick = function (event) {
          event.preventDefault();
          window.focus();
        };
      }

      return () => {
        stopCallRingtone();
      };
    }

    stopCallRingtone();
    resetTimer();
    return undefined;
  }, [incomingCall]);

  useEffect(() => {
    if (currentCall || outgoingCall) {
      stopCallRingtone();
    }
  }, [currentCall, outgoingCall]);

  const expandElectronForCall = () => {
    if (
      window.__portalUI &&
      typeof window.__portalUI.exitCompact === "function"
    ) {
      window.__portalUI.exitCompact();
    }

    const desktop = window.wunityDesktop;
    if (!desktop) return;

    const present = () => {
      if (typeof desktop.presentForCall === "function") {
        return desktop.presentForCall();
      }
      const tasks = [];
      if (typeof desktop.setWindowMode === "function") {
        tasks.push(desktop.setWindowMode("full"));
      }
      if (typeof desktop.showWindow === "function") {
        tasks.push(desktop.showWindow());
      }
      return Promise.all(tasks);
    };

    // Tray / odak yarışına karşı birkaç kez zorla öne getir
    Promise.resolve(present()).catch(() => {});
    setTimeout(() => {
      Promise.resolve(present()).catch(() => {});
    }, 150);
    setTimeout(() => {
      Promise.resolve(present()).catch(() => {});
    }, 600);
  };

  const handleAnswerCall = () => {
    stopCallRingtone();
    expandElectronForCall();
    const payload = incomingCall.payload || {};
    dispatch(callActions.setIncomingCall(undefined));
    dispatch(
      callActions.answerCall(
        payload.group.id,
        payload.roomId,
        incomingCall.fromId,
        payload.video,
        payload.screen
      )
    );
  };

  const handleRejectCall = () => {
    stopCallRingtone();
    dispatch(callActions.setIncomingCall(undefined));
    dispatch(
      callActions.rejectCall(
        incomingCall.payload.group.id,
        incomingCall.payload.roomId,
        incomingCall.payload.video
      )
    );
  };

  const handleEndCall = () => {
    stopCallRingtone();
    dispatch(callActions.endCall(outgoingCall.group.id, outgoingCall.roomId));
    dispatch(callActions.setOutgoingCall(undefined));
  };

  function useInterval(callback, delay) {
    const savedCallback = useRef();

    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);
    useEffect(() => {
      function tick() {
        savedCallback.current();
      }
      if (delay !== null) {
        let id = setInterval(tick, delay);
        return () => clearInterval(id);
      }
    }, [delay]);
  }

  const resetTimer = () => {
    setSecond(0);
    setMilisecond(0);
  };

  const timer = () => {
    setMilisecond(milisecond + 10);
    if (milisecond === 1000) {
      setMilisecond(0);
      setSecond(second + 1);
    }
  };
  useInterval(() => {
    if (outgoingCall || incomingCall) timer();
  }, 10);

  useEffect(() => {
    if (second >= 30 && currentCall === undefined) {
      resetTimer();
      stopCallRingtone();
      dispatch(callActions.setIncomingCall(undefined));
      dispatch(callActions.setOutgoingCall(undefined));
      if (outgoingCall) {
        dispatch(
          callActions.markNoAnswerCall(
            outgoingCall.group.id,
            outgoingCall.roomId,
            outgoingCall.video
          )
        );
        dispatch(
          requestActions.notify({
            text: intl.formatMessage({
              id: "messaging.userNotAnswering",
              defaultMessage: "User is not answering.",
            }),
          })
        );
      }
    }
  }, [second]);

  useEffect(() => {
    if (!outgoingCall) {
      resetTimer();
    }
  }, [outgoingCall]);

  useEffect(() => {
    if (currentCall) {
      stopCallRingtone();
      expandElectronForCall();
      const isScreenCall = Boolean(currentCall.screen);
      const shouldShareScreen = Boolean(currentCall.startScreenShare);
      roomClient.join({
        roomId: currentCall.roomId,
        roomName: "",
        joinAudio: !isScreenCall,
        joinVideo: isScreenCall ? false : Boolean(currentCall.video),
        // Sadece arayan otomatik ekran paylaşır; cevaplayan izler
        joinScreen: shouldShareScreen,
        call: true,
      });
      resetTimer();
    }
  }, [currentCall]);

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const prevRoomState = usePrevious(room?.spotlights);
  useEffect(() => {
    if (
      currentCall?.group?.type === "individual" &&
      prevRoomState?.length === 1 &&
      room?.spotlights?.length === 0
    ) {
      dispatch(callActions.endCall(currentCall.group.id, currentCall.roomId));
      dispatch(callActions.setCurrentCall(undefined));
    }
  }, [currentCall, room]);

  return (
    <div>
      {incomingCall && (
        <Dialog
          open
          BackdropProps={{ className: classes.backdrop }}
          PaperProps={{ className: classes.paper, elevation: 0 }}
        >
          <DialogContent className={classes.content}>
            <InComingOutGoingCall
              data={incomingCall}
              type={"inComing"}
              onReject={handleRejectCall}
              onAnswer={handleAnswerCall}
              onEnd={handleEndCall}
            />
          </DialogContent>
        </Dialog>
      )}

      {outgoingCall && (
        <Dialog
          open
          BackdropProps={{ className: classes.backdrop }}
          PaperProps={{ className: classes.paper, elevation: 0 }}
        >
          <DialogContent className={classes.content}>
            <InComingOutGoingCall
              data={outgoingCall}
              type={"outGoing"}
              onReject={() => {}}
              onAnswer={() => {}}
              onEnd={handleEndCall}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CallView;
